import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/** The nearest ancestor that scrolls vertically — the widget's campaign layout scroll region. */
export function scrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Whether a column may be pinned while its sibling scrolls: only when it fits inside the scroll
 * region as it stands. A sticky column taller than the viewport pins its top and hides its tail
 * until the very end of the scroll — worse than plain scrolling — so a column that needs more
 * height than the region offers simply scrolls with the page. Re-measured whenever either the
 * column or the region resizes (a feed row arriving, the panel changing size).
 *
 * Also reports the region's top padding: a pinned element sits that far below the scrollport's
 * edge (`top: 0` is clamped to the content box), and content scrolls through that strip unless
 * something paints it — the padding differs between the on-site page and the in-game embed, so
 * it is measured, not assumed.
 */
export function useStickyIfFits<T extends HTMLElement>(): [
  RefObject<T | null>,
  boolean,
  number,
] {
  const ref = useRef<T | null>(null);
  const [fits, setFits] = useState(false);
  const [padTop, setPadTop] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = scrollParent(el);
    if (!container) return;

    const measure = () => {
      const { paddingTop, paddingBottom } = getComputedStyle(container);
      const room =
        container.clientHeight -
        parseFloat(paddingTop) -
        parseFloat(paddingBottom);
      setFits(el.offsetHeight <= room);
      setPadTop(parseFloat(paddingTop) || 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return [ref, fits, padTop];
}

/**
 * How far below the scrollport's top edge an element rests when the region is scrolled to the
 * top — i.e. exactly the strip that scrolled content would show through above it once it is
 * pinned with `top: 0`. Measured, not assumed: the on-site page and the in-game embed put
 * different things (padding, notices) above the same element. Re-measured on resize.
 */
export function useRestingOffset<T extends HTMLElement>(): [
  RefObject<T | null>,
  number,
] {
  const ref = useRef<T | null>(null);
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = scrollParent(el);
    if (!container) return;

    const measure = () => {
      // Distance in the container's scroll coordinates, independent of the current scroll
      // position — and of whether the element is pinned right now: while pinned it sits at
      // `top: 0`, so its box moves with the viewport, and subtracting the scroll would not do.
      // Its unpinned position is its offset from the container's content, walked up the tree.
      let top = 0;
      let node: HTMLElement | null = el;
      while (node && node !== container) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      setOffset(Math.max(0, Math.round(top)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return [ref, offset];
}
